/**
 * AuditFlow — engagement management for Indian CA practice.
 *
 * Based on Inflo's architecture but adapted for Indian Standards on Auditing
 * (SAs) issued by the ICAI, not ISAs. Key India-specific elements:
 *
 *   - Engagement letters under SA 210
 *   - CARO 2020 reporting for Companies Act audits
 *   - Form 3CB / 3CD for tax audits under section 44AB
 *   - SFR (Statutory Financial Reporting) disclosures under Schedule III
 *   - ICAI Peer Review requirements
 *   - Materiality computed under SA 320
 *   - Journal entry testing under SA 240 (fraud risk)
 *
 * The engagement lifecycle always originates here. ClientDesk reflects it.
 */

import type { AuditOpinion, FindingSeverity } from "./bridge";

// --------------------------------------------------- Engagement lifecycle

export type EngagementStage =
  | "acceptance"     // SA 220 / SA 210 — pre-engagement checks and letter
  | "planning"       // SA 300 — understand entity, set materiality, risk assessment
  | "fieldwork"      // SA 500 series — evidence gathering, testing
  | "review"         // SA 220 — manager and partner review
  | "completion"     // SA 700 series — finalisation, representation letter
  | "signed-off"     // Audit report issued
  | "archived"       // ICAI peer review ready
  | "dropped";

export type EngagementType =
  | "statutory-audit"       // Companies Act 2013 — mandatory for companies
  | "tax-audit-44ab"        // Income-tax Act — turnover threshold
  | "tax-audit-44ad"        // Presumptive taxation
  | "gst-audit"             // GST — now removed but legacy engagements exist
  | "internal-audit"
  | "limited-review"        // Listed entities — SA 910
  | "agreed-procedures"     // SA 920
  | "compilation"           // SA 930
  | "due-diligence"
  | "certification"
  | "special-purpose";

export const ENGAGEMENT_TYPE_LABELS: Record<EngagementType, string> = {
  "statutory-audit": "Statutory audit (Companies Act)",
  "tax-audit-44ab": "Tax audit — section 44AB",
  "tax-audit-44ad": "Tax audit — section 44AD",
  "gst-audit": "GST audit (legacy)",
  "internal-audit": "Internal audit",
  "limited-review": "Limited review",
  "agreed-procedures": "Agreed-upon procedures",
  compilation: "Compilation",
  "due-diligence": "Due diligence",
  certification: "Certification / report",
  "special-purpose": "Special purpose"
};

export const STAGE_LABELS: Record<EngagementStage, string> = {
  acceptance: "Acceptance",
  planning: "Planning",
  fieldwork: "Fieldwork",
  review: "Review",
  completion: "Completion",
  "signed-off": "Signed off",
  archived: "Archived",
  dropped: "Dropped"
};

export const STAGE_ORDER: EngagementStage[] = [
  "acceptance", "planning", "fieldwork", "review", "completion", "signed-off", "archived"
];

// --------------------------------------------------- Acceptance stage

export interface IndependenceCheck {
  checkedBy: string;
  checkedAt: string;
  /** Self-review, advocacy, familiarity, intimidation, self-interest. */
  threatsIdentified: string[];
  safeguardsApplied: string[];
  independent: boolean;
  notes?: string;
}

export interface AcceptanceChecklist {
  /** SA 220 — firm's quality management policies. */
  qualityManagementReviewed: boolean;
  /** SA 210 — client's financial reporting framework agreed. */
  frameworkAgreed: boolean;
  /** SA 220 — independence confirmed for all team members. */
  independenceConfirmed: boolean;
  independenceChecks: IndependenceCheck[];
  /** Prior auditor communication (SA 300) for first-year engagements. */
  priorAuditorCommunicated?: boolean;
  /** ICAI ethics — contingent fees, commissions. */
  ethicsChecked: boolean;
  /** Client background check. */
  clientBackgroundChecked: boolean;
  /** Significant risk areas identified at pre-engagement. */
  preEngagementRisks: string[];
  engagementLetterIssued: boolean;
  engagementLetterSignedAt?: string;
  managementRepresentativeSignatory?: string;
  accepted: boolean;
  acceptedBy?: string;
  acceptedAt?: string;
}

// ---------------------------------------------------- Planning stage

export type MaterialityBasis =
  | "profit-before-tax"
  | "turnover"
  | "total-assets"
  | "equity"
  | "gross-profit"
  | "expenditure";

export interface MaterialityCalculation {
  basis: MaterialityBasis;
  baseAmount: number;
  /** Percentage applied to the base. SA 320 — typically 5% of PBT, 0.5-1% of turnover. */
  percentageApplied: number;
  overallMateriality: number;
  /** Performance materiality — typically 60-75% of overall. */
  performanceMateriality: number;
  /** For clearly trivial threshold — typically 3-5% of overall materiality. */
  clearlyTrivialThreshold: number;
  /** Where a lower threshold applies (related parties, compliance). */
  specificMaterialityItems?: Array<{ area: string; amount: number; rationale: string }>;
  calculatedBy: string;
  approvedBy?: string;
  calculatedAt: string;
  notes?: string;
}

export type RiskLevel = "high" | "significant" | "medium" | "low";
export type AssertionRisk = "completeness" | "existence" | "accuracy" | "valuation" | "cut-off" | "classification" | "occurrence" | "rights-and-obligations" | "presentation";

export interface RiskArea {
  id: string;
  area: string;
  /** Financial statement line item or disclosure. */
  fsItem: string;
  assertions: AssertionRisk[];
  inherentRisk: RiskLevel;
  controlRisk: RiskLevel;
  /** Assessed risk of material misstatement. */
  rmmLevel: RiskLevel;
  /** SA 315 — significant risk. */
  isSignificantRisk: boolean;
  /** SA 240 — fraud risk. */
  isFraudRisk: boolean;
  /** SA 570 — going concern. */
  isGoingConcernRisk?: boolean;
  riskDescription: string;
  auditResponse: string;
  plannedProcedures: string[];
  identifiedBy: string;
  identifiedAt: string;
  managementResponse?: string;
}

export interface TrialBalanceItem {
  accountCode: string;
  accountName: string;
  currentYearAmount: number;
  priorYearAmount: number;
  movement: number;
  movementPercent: number;
  /** Whether the auditor has flagged this line for attention. */
  flagged: boolean;
  flagReason?: string;
  /** Set when this line was imported from LedzerlyBooks. */
  importedFromBooks?: boolean;
}

export interface TrialBalance {
  engagementId: string;
  asAtDate: string;
  items: TrialBalanceItem[];
  totalDebits: number;
  totalCredits: number;
  balanced: boolean;
  source: "books-import" | "manual" | "client-upload";
  importedAt?: string;
  importedBy?: string;
  adjustmentEntries?: Array<{ description: string; debit: number; credit: number; account: string }>;
}

// ---------------------------------------------------- Workpapers

export type WorkpaperStatus =
  | "not-started"
  | "in-progress"
  | "completed"
  | "reviewed"
  | "signed-off"
  | "not-applicable";

export type WorkpaperSection =
  | "planning"
  | "client-understanding"
  | "controls"
  | "risk-assessment"
  | "substantive"
  | "analytical"
  | "completion"
  | "caro"
  | "tax-audit"
  | "group";

export interface WorkpaperProcedure {
  id: string;
  description: string;
  /** Which SA this procedure is responding to. */
  saReference?: string;
  /** Which risk area(s) this addresses. */
  addressesRiskIds?: string[];
  assertion?: AssertionRisk;
  completed: boolean;
  completedBy?: string;
  completedAt?: string;
  /** Evidence reference — document or sample. */
  evidenceRef?: string;
  conclusion?: string;
  exceptions?: string;
  notes?: string;
}

export interface Workpaper {
  id: string;
  engagementId: string;
  section: WorkpaperSection;
  /** Reference number, e.g. A-1, B-2.1. */
  reference: string;
  title: string;
  status: WorkpaperStatus;
  /** SA this workpaper responds to. */
  saReferences: string[];
  procedures: WorkpaperProcedure[];
  preparedBy?: string;
  preparedAt?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewComments?: string;
  signedOffBy?: string;
  signedOffAt?: string;
  attachedDocumentIds?: string[];
  /** Links to PBC requests whose responses support this workpaper. */
  pbcRequestIds?: string[];
  notes?: string;
}

// ---------------------------------------------------- PBC (Prepared by Client)

export type PbcStatus = "requested" | "received" | "reviewed" | "accepted" | "rejected";

export interface PbcRequest {
  id: string;
  engagementId: string;
  workpaperId?: string;
  description: string;
  /** Why this document is needed. */
  purpose?: string;
  status: PbcStatus;
  requestedFrom: string;
  requestedAt: string;
  dueDate: string;
  receivedAt?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  rejectionReason?: string;
  documentId?: string;
  /** Client-facing label shown in the portal. */
  clientLabel?: string;
}

// ---------------------------------------------------- Sampling

export type SamplingMethod =
  | "monetary-unit"     // MUS — probability proportionate to size
  | "random"
  | "systematic"
  | "haphazard"
  | "judgement"
  | "full-population";  // No sampling — 100% testing

export interface SampleSelection {
  id: string;
  engagementId: string;
  workpaperId?: string;
  population: string;
  populationSize: number;
  totalValue: number;
  method: SamplingMethod;
  sampleSize: number;
  samplingInterval?: number;
  randomSeed?: number;
  tolerableError: number;
  expectedError: number;
  selectedItems: Array<{ reference: string; amount: number; description?: string }>;
  selectedAt: string;
  selectedBy: string;
  conclusion?: string;
  projectedError?: number;
}

// ---------------------------------------------------- Findings

export interface AuditFinding {
  id: string;
  engagementId: string;
  workpaperId?: string;
  riskAreaId?: string;
  title: string;
  description: string;
  severity: FindingSeverity;
  category: "misstatement" | "control-deficiency" | "compliance" | "disclosure" | "going-concern" | "fraud-indicator" | "observation";
  /** Financial impact, where quantifiable. */
  amount?: number;
  /** Which assertions are affected. */
  assertions: AssertionRisk[];
  /** Management's response. */
  managementResponse?: string;
  managementResponseAt?: string;
  managedBy?: string;
  resolution?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  status: "open" | "resolved" | "accepted-by-management" | "reported";
  /** Whether this finding affects the audit opinion. */
  affectsOpinion: boolean;
  raisedBy: string;
  raisedAt: string;
  saReference?: string;
}

// ---------------------------------------------------- CARO 2020 (India-specific)

/**
 * Companies (Auditor's Report) Order, 2020. Mandatory for companies above
 * the prescribed thresholds. The CARO report is an annex to the audit report
 * and covers 21 clauses. Each clause has a standard question; the auditor
 * answers Yes/No/Not applicable with reasons where required.
 */
export interface CaroClause {
  clauseNumber: string;
  heading: string;
  subItems?: Array<{
    label: string;
    response: "yes" | "no" | "not-applicable" | "pending";
    remarks?: string;
  }>;
  response: "yes" | "no" | "not-applicable" | "pending";
  remarks?: string;
  completedBy?: string;
  completedAt?: string;
}

export const CARO_2020_CLAUSES: Array<{ clauseNumber: string; heading: string }> = [
  { clauseNumber: "i(a)", heading: "Maintained proper records showing full particulars of tangible fixed assets" },
  { clauseNumber: "i(b)", heading: "Maintained proper records showing full particulars of intangible assets" },
  { clauseNumber: "i(c)", heading: "Physical verification of fixed assets — frequency and material discrepancies" },
  { clauseNumber: "i(d)", heading: "Revaluation of fixed assets / right-of-use assets" },
  { clauseNumber: "i(e)", heading: "Proceedings initiated or pending against the company for holding benami property" },
  { clauseNumber: "ii(a)", heading: "Physical verification of inventory — frequency and material discrepancies" },
  { clauseNumber: "ii(b)", heading: "Sanctioned working capital limits from banks / financial institutions" },
  { clauseNumber: "iii(a)", heading: "Investments / loans / advances / guarantees — disclosure" },
  { clauseNumber: "iii(b)", heading: "Investments in / loans to subsidiaries, associates and related parties" },
  { clauseNumber: "iii(c)", heading: "Loans to directors / KMPs — approval under section 185" },
  { clauseNumber: "iii(d)", heading: "Unconditional demand repayable on demand — repayment schedule" },
  { clauseNumber: "iv", heading: "Compliance with sections 185 and 186 of the Companies Act" },
  { clauseNumber: "v", heading: "Acceptance of deposits — compliance with RBI directives and Companies Act" },
  { clauseNumber: "vi", heading: "Cost records prescribed by the Central Government — maintenance" },
  { clauseNumber: "vii(a)", heading: "Undisputed statutory dues — regularity of deposit" },
  { clauseNumber: "vii(b)", heading: "Disputed statutory dues — amounts not deposited on account of disputes" },
  { clauseNumber: "viii", heading: "Unrecorded income surrendered during search / survey" },
  { clauseNumber: "ix(a)", heading: "Default in repayment of borrowings — details" },
  { clauseNumber: "ix(b)", heading: "Declared willful defaulter by bank / financial institution" },
  { clauseNumber: "ix(c)", heading: "Term loans — applied for the purpose for which raised" },
  { clauseNumber: "ix(d)", heading: "Funds raised on short-term basis used for long-term purposes" },
  { clauseNumber: "ix(e)", heading: "Funds taken from subsidiaries / associates / any other persons for specific purpose" },
  { clauseNumber: "ix(f)", heading: "Funds raised through loans / borrowings from banks used to meet obligations of other entities" },
  { clauseNumber: "x(a)", heading: "Money raised by initial public offer / further public offer — purpose" },
  { clauseNumber: "x(b)", heading: "Preferential allotment / private placement — compliance" },
  { clauseNumber: "xi(a)", heading: "Fraud by or on the company — material / noticed / reported" },
  { clauseNumber: "xi(b)", heading: "Auditor's report under section 143(12) — filed with Central Government" },
  { clauseNumber: "xi(c)", heading: "Whistle blower complaints received — dealt with" },
  { clauseNumber: "xii", heading: "Nidhi company — compliance" },
  { clauseNumber: "xiii", heading: "Related party transactions — compliance with sections 177 and 188" },
  { clauseNumber: "xiv(a)", heading: "Internal audit system — commensurate with size and nature of business" },
  { clauseNumber: "xiv(b)", heading: "Internal audit reports — considered by statutory auditors" },
  { clauseNumber: "xv", heading: "Non-cash transactions with directors — compliance with section 192" },
  { clauseNumber: "xvi(a)", heading: "Registration under section 45-IA of the RBI Act, 1934" },
  { clauseNumber: "xvi(b)", heading: "Core investment company — registration with RBI" },
  { clauseNumber: "xvi(c)", heading: "Number of layers prescribed under section 2(87) exceeded" },
  { clauseNumber: "xvii", heading: "Cash losses — current year and immediately preceding year" },
  { clauseNumber: "xviii", heading: "Resignation of statutory auditors — reasons" },
  { clauseNumber: "xix", heading: "Ability to meet its liabilities and going concern" },
  { clauseNumber: "xx(a)", heading: "CSR obligations — transfer to Unspent CSR Account" },
  { clauseNumber: "xx(b)", heading: "Ongoing projects — transfer to separate account under section 135(6)" },
  { clauseNumber: "xxi", heading: "Qualifications / adverse remarks by secretarial auditors in secretarial audit report" }
];

// ---------------------------------------------------- Form 3CD (Tax audit)

/**
 * Form 3CD is the statement of particulars required to be furnished under
 * section 44AB. It has 41 clauses. Rather than enumerate all 41 (which would
 * be a product in itself), AuditFlow tracks which clauses are complete, which
 * have disclosures, and flags those with financial figures for review.
 */
export interface Form3CDClause {
  clauseNumber: string;
  heading: string;
  applicable: boolean;
  response?: string;
  amount?: number;
  completed: boolean;
  hasDisclosure: boolean;
  reviewNote?: string;
  completedBy?: string;
  completedAt?: string;
}

export const FORM_3CD_KEY_CLAUSES = [
  { clauseNumber: "13", heading: "Method of accounting and change in method" },
  { clauseNumber: "14", heading: "Valuation of closing stock" },
  { clauseNumber: "17", heading: "Deemed profits under various provisions" },
  { clauseNumber: "18", heading: "Income exempt under sections 10, 10AA, 11 and 12" },
  { clauseNumber: "19", heading: "Depreciation as per Income Tax Act" },
  { clauseNumber: "20", heading: "Deductions under sections 33AB, 33ABA, 35, 35ABB" },
  { clauseNumber: "21", heading: "Amounts inadmissible under section 40A and 43B" },
  { clauseNumber: "26", heading: "Liability of assessee as agent" },
  { clauseNumber: "29a", heading: "Particulars of deemed dividend under section 2(22)(e)" },
  { clauseNumber: "30a", heading: "TDS/TCS — defaults and disclosures" },
  { clauseNumber: "31", heading: "Receipt of amounts by cash exceeding prescribed limits" },
  { clauseNumber: "32", heading: "Turnover, gross profit, net profit ratios" },
  { clauseNumber: "34", heading: "Payments under section 43B" },
  { clauseNumber: "40", heading: "Details of MSME payments and disallowance under section 43B(h) / 37(2)(g)" },
  { clauseNumber: "41", heading: "Details of crypto-currency / virtual digital assets" }
];

// ---------------------------------------------------- Completion

export interface RepresentationLetter {
  engagementId: string;
  date: string;
  /** SA 580 — written representations. */
  representations: Array<{
    clause: string;
    text: string;
    obtained: boolean;
    obtainedAt?: string;
  }>;
  signedBy?: string;
  signedAt?: string;
  signedInCapacity?: string;
}

export interface SubsequentEventsReview {
  engagementId: string;
  reviewedUpTo: string;
  events: Array<{
    id: string;
    description: string;
    type: "adjusting" | "non-adjusting";
    disclosed: boolean;
    amount?: number;
    notes?: string;
  }>;
  reviewedBy?: string;
  reviewedAt?: string;
}

export interface GoingConcernAssessment {
  engagementId: string;
  indicators: Array<{
    type: "financial" | "operational" | "other";
    description: string;
    mitigant?: string;
  }>;
  conclusion: "no-doubt" | "material-uncertainty" | "not-going-concern";
  notes?: string;
  assessedBy?: string;
  assessedAt?: string;
}

// ---------------------------------------------------- The engagement

export interface AuditEngagement {
  id: string;
  clientId: string;
  clientName: string;
  type: EngagementType;
  financialYear: string;
  periodEnd: string;
  stage: EngagementStage;
  partner: string;
  manager?: string;
  team: string[];
  acceptance?: AcceptanceChecklist;
  materiality?: MaterialityCalculation;
  trialBalance?: TrialBalance;
  riskAreas: RiskArea[];
  workpapers: Workpaper[];
  pbcRequests: PbcRequest[];
  findings: AuditFinding[];
  caro?: CaroClause[];
  form3cd?: Form3CDClause[];
  representationLetter?: RepresentationLetter;
  subsequentEvents?: SubsequentEventsReview;
  goingConcern?: GoingConcernAssessment;
  opinion?: AuditOpinion;
  reportDate?: string;
  signingDate?: string;
  udinNumber?: string;
  /** ICAI unique document identification number for the audit report. */
  createdAt: string;
  updatedAt?: string;
  archivedAt?: string;
}

// ---------------------------------------------------- Completion percentage

export const engagementCompletionPercent = (engagement: AuditEngagement): number => {
  const checks: boolean[] = [
    Boolean(engagement.acceptance?.accepted),
    Boolean(engagement.materiality),
    Boolean(engagement.trialBalance),
    engagement.riskAreas.length > 0,
    engagement.workpapers.filter((wp) => wp.status === "signed-off" || wp.status === "not-applicable").length >=
      Math.max(1, engagement.workpapers.length * 0.5),
    engagement.pbcRequests.filter((pbc) => pbc.status === "accepted" || pbc.status === "received").length >=
      Math.max(0, engagement.pbcRequests.length),
    engagement.findings.filter((f) => f.status === "open").length === 0,
    Boolean(engagement.representationLetter?.signedAt),
    Boolean(engagement.goingConcern),
    Boolean(engagement.opinion)
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
};

/** Which stages are still ahead of the current one. */
export const remainingStages = (engagement: AuditEngagement): EngagementStage[] => {
  const current = STAGE_ORDER.indexOf(engagement.stage);
  return current === -1 ? STAGE_ORDER : STAGE_ORDER.slice(current + 1);
};

/** Whether the engagement can move to the next stage. */
export interface StageReadiness {
  ready: boolean;
  blockers: string[];
  warnings: string[];
}

export const stageReadiness = (
  engagement: AuditEngagement,
  toStage: EngagementStage
): StageReadiness => {
  const blockers: string[] = [];
  const warnings: string[] = [];

  switch (toStage) {
    case "planning":
      if (!engagement.acceptance?.accepted) blockers.push("Engagement letter must be accepted before planning begins.");
      if (!engagement.acceptance?.independenceConfirmed) blockers.push("Independence must be confirmed for all team members.");
      break;
    case "fieldwork":
      if (!engagement.materiality) blockers.push("Materiality must be calculated and approved.");
      if (!engagement.trialBalance) blockers.push("Trial balance must be uploaded or imported.");
      if (engagement.riskAreas.length === 0) blockers.push("At least one risk area must be identified.");
      break;
    case "review":
      const openPbc = engagement.pbcRequests.filter((pbc) => pbc.status === "requested").length;
      if (openPbc > 0) warnings.push(`${openPbc} PBC request(s) still outstanding from the client.`);
      const incompleteWp = engagement.workpapers.filter((wp) => wp.status === "in-progress" || wp.status === "not-started").length;
      if (incompleteWp > 0) blockers.push(`${incompleteWp} workpaper(s) not yet complete.`);
      break;
    case "completion":
      const unresolvedFindings = engagement.findings.filter((f) => f.status === "open").length;
      if (unresolvedFindings > 0) blockers.push(`${unresolvedFindings} finding(s) still open — resolve or accept before completion.`);
      if (!engagement.goingConcern) blockers.push("Going concern assessment is required.");
      if (!engagement.representationLetter?.signedAt) warnings.push("Representation letter not yet signed.");
      break;
    case "signed-off":
      if (!engagement.opinion) blockers.push("Audit opinion has not been decided.");
      if (!engagement.representationLetter?.signedAt) blockers.push("Representation letter must be signed before sign-off.");
      if (engagement.type === "statutory-audit" && !engagement.caro?.every((clause) => clause.response !== "pending")) {
        warnings.push("Some CARO clauses are still pending.");
      }
      if ((engagement.type === "tax-audit-44ab") && !engagement.form3cd?.every((clause) => !clause.applicable || clause.completed)) {
        blockers.push("Form 3CD has incomplete clauses.");
      }
      if (!engagement.udinNumber) blockers.push("UDIN must be generated and recorded before the report is issued.");
      break;
    default:
      break;
  }

  return { ready: blockers.length === 0, blockers, warnings };
};
